// src/supplier/pages/SupplierBusinesses.jsx
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supplierBusinessAPI, supplierProductAPI, supplierRFQAPI, supplierPOAPI, supplierDeliveryAPI } from "../services/supplierApi";
import {
  Building2, Loader2, ChevronLeft, Package, FileText, ShoppingCart, Truck,
  AlertCircle, ArrowRight, Search, Filter, Clock, Star
} from "lucide-react";
import { timeAgo } from "../../utils/timeAgo";
import { fmtPeso } from "../../utils/formatters";
import { StatusBadge } from "../../components/erp";
import ShopSelect from "../../components/ShopSelect";
import { getDeliveryLabel, getPOLabel, getRFQLabel } from "../../utils/documentLabels";

export default function SupplierBusinesses() {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Detail state
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [tabData, setTabData] = useState({ products: null, rfqs: null, orders: null, deliveries: null });
  const [tabLoading, setTabLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      const res = await supplierBusinessAPI.list(params);
      setBusinesses(res?.data || []);
    } catch { setBusinesses([]); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Load detail when businessId param is present
  useEffect(() => {
    if (!businessId) {
      setDetail(null);
      return;
    }
    (async () => {
      setDetailLoading(true);
      setActiveTab("overview");
      setTabData({ products: null, rfqs: null, orders: null, deliveries: null });
      try {
        const res = await supplierBusinessAPI.getById(businessId);
        setDetail(res?.data || null);
      } catch { setDetail(null); }
      finally { setDetailLoading(false); }
    })();
  }, [businessId]);

  // Load tab data when switching tabs in detail view
  useEffect(() => {
    if (!businessId || activeTab === "overview") return;
    (async () => {
      setTabLoading(true);
      try {
        if (activeTab === "products" && !tabData.products) {
          const res = await supplierProductAPI.list({ businessId, limit: 200 });
          setTabData(prev => ({ ...prev, products: res?.data || [] }));
        } else if (activeTab === "rfqs" && !tabData.rfqs) {
          const res = await supplierRFQAPI.list({ limit: 200 });
          // Filter client-side by business since API returns all
          const bizRfqs = (res?.data || []).filter(r => String(r.business_id) === String(businessId) || r.business_name === detail?.business_name);
          setTabData(prev => ({ ...prev, rfqs: bizRfqs }));
        } else if (activeTab === "orders" && !tabData.orders) {
          const res = await supplierPOAPI.list({ limit: 200 });
          const bizOrders = (res?.data || []).filter(o => String(o.business_id) === String(businessId) || o.business_name === detail?.business_name);
          setTabData(prev => ({ ...prev, orders: bizOrders }));
        } else if (activeTab === "deliveries" && !tabData.deliveries) {
          const res = await supplierDeliveryAPI.list({ limit: 200 });
          const bizDeliveries = (res?.data || []).filter(d => String(d.business_id) === String(businessId) || d.business_name === detail?.business_name);
          setTabData(prev => ({ ...prev, deliveries: bizDeliveries }));
        }
      } catch { /* keep nulls */ }
      finally { setTabLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, activeTab]);

  const filteredBusinesses = businesses.filter(b =>
    !search || b.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  // ────────── Detail view ──────────
  if (businessId) {
    if (detailLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      );
    }
    if (!detail) {
      return (
        <div className="space-y-4">
          <button onClick={() => navigate("/supplier/businesses")} className="flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
            <ChevronLeft className="h-3 w-3" /> Back to Businesses
          </button>
          <div className="rounded-xl bg-red-50 p-4 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Business relationship not found.
          </div>
        </div>
      );
    }

    const TABS = [
      { key: "overview",   label: "Overview",   icon: Building2 },
      { key: "products",   label: "Products",   icon: Package },
      { key: "rfqs",       label: "RFQs",       icon: FileText },
      { key: "orders",     label: "Orders",     icon: ShoppingCart },
      { key: "deliveries", label: "Deliveries", icon: Truck },
    ];

    return (
      <div className="operations-page max-w-4xl">
        <button onClick={() => navigate("/supplier/businesses")} className="flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
          <ChevronLeft className="h-3 w-3" /> Back to Businesses
        </button>

        {/* Header card */}
        <div className="operations-surface p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-950/30">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 truncate">{detail.business_name}</h2>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  Connected {detail.accepted_at ? new Date(detail.accepted_at).toLocaleDateString() : "—"}
                </p>
              </div>
            </div>
            <StatusBadge status={detail.relationship_status} className="flex-shrink-0" />
          </div>
          {detail.notes && (
            <div className="mt-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-3 text-xs text-neutral-600 dark:text-neutral-300">
              <p className="text-[10px] text-neutral-400 uppercase tracking-wide font-medium mb-1">Notes</p>
              {detail.notes}
            </div>
          )}
          {detail.rating && (
            <div className="mt-2 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <Star className="h-3 w-3" /> Rating: {detail.rating}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-neutral-200 dark:border-neutral-700 pb-px">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-t-lg px-3 py-2 text-xs font-medium transition ${
                  isActive
                    ? "bg-white dark:bg-neutral-900 text-blue-600 dark:text-blue-400 border border-b-0 border-neutral-200 dark:border-neutral-700"
                    : "text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="min-h-[200px]">
          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            </div>
          ) : activeTab === "overview" ? (
            <div className="operations-surface p-5">
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-3">Business Overview</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 p-3">
                  <p className="text-neutral-400 mb-0.5">Status</p>
                  <p className="font-semibold text-neutral-700 dark:text-neutral-200">{detail.relationship_status}</p>
                </div>
                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 p-3">
                  <p className="text-neutral-400 mb-0.5">Business Status</p>
                  <p className="font-semibold text-neutral-700 dark:text-neutral-200">{detail.business_status || "—"}</p>
                </div>
                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 p-3">
                  <p className="text-neutral-400 mb-0.5">Connected Since</p>
                  <p className="font-semibold text-neutral-700 dark:text-neutral-200">{detail.accepted_at ? new Date(detail.accepted_at).toLocaleDateString() : "Pending"}</p>
                </div>
                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 p-3">
                  <p className="text-neutral-400 mb-0.5">Relationship Started</p>
                  <p className="font-semibold text-neutral-700 dark:text-neutral-200">{detail.relationship_created_at ? new Date(detail.relationship_created_at).toLocaleDateString() : "—"}</p>
                </div>
              </div>
            </div>
          ) : activeTab === "products" ? (
            <BusinessTabProducts products={tabData.products} />
          ) : activeTab === "rfqs" ? (
            <BusinessTabRFQs rfqs={tabData.rfqs} navigate={navigate} />
          ) : activeTab === "orders" ? (
            <BusinessTabOrders orders={tabData.orders} navigate={navigate} />
          ) : activeTab === "deliveries" ? (
            <BusinessTabDeliveries deliveries={tabData.deliveries} navigate={navigate} />
          ) : null}
        </div>
      </div>
    );
  }

  // ────────── List view ──────────
  return (
    <div className="operations-page">
      <div className="operations-page-header">
        <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Connected Businesses</h1>
        <span className="text-xs text-neutral-400">{filteredBusinesses.length} of {businesses.length}</span>
      </div>

      {/* Search + Filter bar */}
      <div className="operations-toolbar">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            placeholder="Search businesses…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
          />
        </div>
        <div className="relative">
          <Filter className="pointer-events-none absolute left-3 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
          <ShopSelect
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { value: "", label: "All statuses" },
              { value: "ACTIVE", label: "Active" },
              { value: "PENDING", label: "Pending" },
              { value: "INACTIVE", label: "Inactive" },
              { value: "BLOCKED", label: "Blocked" },
            ]}
            ariaLabel="Filter businesses by status"
            align="start"
            minPanelWidth={176}
            triggerClassName="min-h-10 w-44 rounded-xl py-2 pl-9 pr-3 text-sm"
            panelClassName="py-1"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : filteredBusinesses.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
          <Building2 className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {businesses.length === 0 ? "No connected businesses" : "No businesses match your search"}
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            {businesses.length === 0
              ? "When businesses add you as a supplier, they'll appear here."
              : "Try adjusting your search or filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredBusinesses.map(biz => (
            <button
              key={biz.business_supplier_id}
              onClick={() => navigate(`/supplier/businesses/${biz.business_id}`)}
              className="operations-surface w-full p-4 text-left transition hover:border-neutral-300 dark:hover:border-neutral-600"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50 truncate">{biz.business_name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusBadge status={biz.relationship_status} />
                  <ArrowRight className="h-3.5 w-3.5 text-neutral-300 dark:text-neutral-600" />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                <span className="flex items-center gap-1" title="Mapped products">
                  <Package className="h-3 w-3" /> {biz.mapped_products_count ?? 0} products
                </span>
                <span className="flex items-center gap-1" title="Active RFQs">
                  <FileText className="h-3 w-3" /> {biz.active_rfqs ?? 0} RFQs
                </span>
                <span className="flex items-center gap-1" title="Active orders">
                  <ShoppingCart className="h-3 w-3" /> {biz.active_orders ?? 0} orders
                </span>
                <span className="flex items-center gap-1" title="Last interaction">
                  <Clock className="h-3 w-3" /> {timeAgo(biz.last_interaction)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-tab components ──────────────────────────────────

function BusinessTabProducts({ products }) {
  if (!products) return null;
  if (products.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
        <Package className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No mapped products</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Products mapped to you for this business will appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {products.map(p => (
        <div key={p.mapping_id} className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50 truncate">{p.product_name}</span>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              p.product_status === "LISTED" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400"
            }`}>
              {p.product_status || "—"}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
            {p.product_sku && <span>SKU: {p.product_sku}</span>}
            {p.unit_cost_cents != null && <span>Cost: {fmtPeso(p.unit_cost_cents)}</span>}
            {p.lead_time_days != null && <span>Lead: {p.lead_time_days}d</span>}
            {p.min_order_qty != null && <span>MOQ: {p.min_order_qty}</span>}
            {p.is_preferred ? <span className="text-amber-600 dark:text-amber-400">★ Preferred</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function BusinessTabRFQs({ rfqs, navigate }) {
  if (!rfqs) return null;
  if (rfqs.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
        <FileText className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No RFQs from this business</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">When this business sends procurement requests, they'll appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {rfqs.map(r => (
        <button
          key={r.id}
          onClick={() => navigate("/supplier/rfqs")}
          className="w-full text-left rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 hover:shadow-md transition"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{getRFQLabel(r)}</span>
            <StatusBadge status={r.status} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
            {r.product_name && <span>{r.product_name}</span>}
            {r.requested_quantity && <span>{r.requested_quantity} units</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function BusinessTabOrders({ orders, navigate }) {
  if (!orders) return null;
  if (orders.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
        <ShoppingCart className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No orders from this business</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Purchase orders from this business will appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {orders.map(o => (
        <button
          key={o.id}
          onClick={() => navigate("/supplier/orders")}
          className="w-full text-left rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 hover:shadow-md transition"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{getPOLabel(o)}</span>
            <StatusBadge status={o.status} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
            {o.product_title && <span>{o.product_title}</span>}
            {o.total_amount_cents && <span>{fmtPeso(o.total_amount_cents)}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function BusinessTabDeliveries({ deliveries, navigate }) {
  if (!deliveries) return null;
  if (deliveries.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
        <Truck className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No deliveries for this business</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Delivery records for this business will appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {deliveries.map(d => (
        <button
          key={d.id}
          onClick={() => navigate("/supplier/deliveries")}
          className="w-full text-left rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 hover:shadow-md transition"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{getDeliveryLabel(d)}</span>
            <StatusBadge status={d.status} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
            <span>{getPOLabel(d)}</span>
            {d.tracking_number && <span className="font-mono">{d.tracking_number}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}
